import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaGraduationCap, 
  FaClock, 
  FaUsers, 
  FaStar,
  FaCheckCircle,
  FaArrowRight,
  FaBookOpen,
  FaCertificate,
  FaCalendarAlt,
  FaChartLine,
  FaQuestionCircle,
  FaFileAlt
} from 'react-icons/fa';

const PageContainer = styled.div`
  margin-top: 200px;
  min-height: calc(100vh - 210px);
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #0a4d68 0%, #088395 50%, #0a2b3c 100%);
  color: white;
  padding: 4rem 5%;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
  
  span {
    color: #ffd966;
    display: block;
    font-size: 2rem;
    font-weight: 300;
    margin-top: 0.5rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto 2rem;
  opacity: 0.95;
  position: relative;
  z-index: 2;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 5%;
`;



const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  .value {
    font-size: 2.2rem;
    font-weight: 700;
    color: #0a4d68;
    margin-bottom: 0.3rem;
  }
  
  .label {
    color: #5a6b7a;
    font-size: 0.9rem;
    text-transform: uppercase;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  color: #0a2b3c;
  margin-bottom: 2rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 4px;
    background: #ffd966;
    border-radius: 2px;
  }
`;

const Grid2Col = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContentBlock = styled.div`
  p {
    color: #4a5f73;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
  }
`;

const ImageWrapper = styled.div`
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.5s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }
`;

const ExamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: 3rem 0;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ExamCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  h3 {
    color: #0a4d68;
    margin-bottom: 1rem;
    font-size: 1.3rem;
  }
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
`;

const ListItem = styled.li`
  margin-bottom: 0.8rem;
  color: #4a5f73;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  svg {
    color: #ffd966;
    font-size: 1rem;
  }
`;

const ObjectiveList = styled.div`
  background: #f8fafc;
  border-radius: 15px;
  padding: 2rem;
  margin: 2rem 0;
  
  h3 {
    color: #0a4d68;
    margin-bottom: 1rem;
  }
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #0a4d68, #088395);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  color: white;
  margin-top: 4rem;
  
  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    opacity: 0.9;
  }
`;

const CTAButton = styled(Link)`
  background: #ffd966;
  color: #0a4d68;
  border: none;
  padding: 1rem 3rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const ExamPrep = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

 

  const objectives = [
    "Demonstrate coding using CPT, ICD-10-CM guidelines",
    "Apply codes to coding scenarios",
    "Determine proper codes for operative notes and auditing",
    "Demonstrate knowledge of healthcare business and processes",
    "Recall key terms and definitions in healthcare organ systems and coding"
  ];

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>
          Exam Preparation
          <span>CPC, CCS, CIC Certification Prep</span>
        </HeroTitle>
        <HeroDescription>
          Comprehensive exam preparation for all major coding certifications. Students review all relevant 
          content areas in preparation for certification examinations. [citation:8]
        </HeroDescription>
      </HeroSection>

      <Container>
       

        <Grid2Col>
          <ContentBlock>
            <SectionTitle>Program Overview</SectionTitle>
            <p>
              Students will review all relevant content areas in preparation for the Academy of 
              Professional Coders Certification (AAPC) examination and AHIMA certification exams. 
              Students will gain an in-depth look at the medical coding process by applying coding 
              guidelines for hospital, outpatient, and physician practice services. [citation:8]
            </p>
            <p>
              Guidelines include ICD-10-CM, CPT, and HCPCS coding methodologies. Upon completion of 
              this course, a date is set for the student to take the four-hour certified professional 
              coder examination. [citation:8]
            </p>
          </ContentBlock>
          
          <ImageWrapper>
            <img 
              src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
              alt="Exam Preparation"
            />
          </ImageWrapper>
        </Grid2Col>

        <ExamGrid>
          <ExamCard>
            <h3>CPC Exam Prep</h3>
            <List>
              <ListItem><FaCheckCircle /> 100+ practice questions</ListItem>
              <ListItem><FaCheckCircle /> Mock exams with timing</ListItem>
              <ListItem><FaCheckCircle /> Anatomy and physiology review</ListItem>
              <ListItem><FaCheckCircle /> E/M coding mastery</ListItem>
              <ListItem><FaCheckCircle /> Surgery coding workshops</ListItem>
            </List>
          </ExamCard>
          
          <ExamCard>
            <h3>CCS Exam Prep</h3>
            <List>
              <ListItem><FaCheckCircle /> Inpatient coding focus</ListItem>
              <ListItem><FaCheckCircle /> DRG assignment practice</ListItem>
              <ListItem><FaCheckCircle /> ICD-10-PCS mastery</ListItem>
              <ListItem><FaCheckCircle /> Medical record abstraction</ListItem>
              <ListItem><FaCheckCircle /> Compliance and regulations</ListItem>
            </List>
          </ExamCard>
        </ExamGrid>

        <ObjectiveList>
          <h3>Course Objectives</h3>
          <List>
            {objectives.map((objective, index) => (
              <ListItem key={index}>
                <FaCheckCircle /> {objective}
              </ListItem>
            ))}
          </List>
        </ObjectiveList>

        <CTASection>
          <h3>Ready to Pass Your Exam?</h3>
          <p>Join our exam prep course and pass with confidence</p>
          <CTAButton to="/register">
            Enroll Now <FaArrowRight />
          </CTAButton>
        </CTASection>
      </Container>
    </PageContainer>
  );
};

export default ExamPrep;